<template>
  <v-form class="course-form mx-auto my-6" @submit.prevent="submitForm">
    <div class="row mb-4">
      <div class="col-8">
        <thumbnail-picker
          label="Thumbnail"
          :current-file="currentFile"
          :current-mobile-static-media="currentMobileStaticMedia"
          @thumbnail-picked="setThumbnail"
          :allowed-file-types="[18]"
          :readonly="readonly"
          one-source-only
          enable-mobile-static-media
          @refresh-current-file="refreshCurrentFile"
        />
        <bh-text-input
          class="mt-2"
          label="Title"
          input-background-class="bhWhite"
          :value="form.title"
          @input-text="updateField('title', $event)"
          :counter="255"
          :show-counter-in-range="10"
          :force-rules="forceRules"
          :readonly="readonly"
          :rules="[
            value => !!value || 'Required.',
            value => (value || '').length <= 255 || 'Max 255 characters',
          ]"
        />
        <bh-text-area
          :value="form.description"
          :counter="30000"
          :show-counter-in-range="10"
          class="mt-2 course-form__description"
          label="Course Description"
          @input-text="updateField('description', $event)"
          :validate="true"
          :force-rules="forceRules"
          :readonly="readonly"
          :rules="[
            value => !!value || 'Required.',
            value => (value || '').length <= 30000 || 'Max 30,000 characters',
          ]"
        />
      </div>
      <div class="col-4">
        <labeled-value
          class="mb-3"
          label="Created:"
          :value="createdDateFormatted"
        />
        <labeled-value class="mb-3" label="Author:" :value="authorName" />
        <labeled-value
          class="mb-3"
          label="Participants:"
          v-if="!hideNonEditable && visibilityStatus !== 'DRAFT'"
        >
          <div class="d-flex align-center">
            <avatars-group
              bgColor="var(--bh-white)"
              :max-count="3"
              :avatars-urls="participantsAvatars"
              :label="assignmentStatusLabel"
            >
              <template v-slot:after>
                <span class="ml-4">{{ participantsCount }}</span>
                <v-btn
                  @click="openAssign"
                  text
                  :elevation="0"
                  color="accent"
                  class="rounded-pill"
                  v-if="!readonly"
                  >Manage</v-btn
                >
              </template>
            </avatars-group>
          </div>
        </labeled-value>
        <labeled-value
          class="mb-3"
          label="Enrolled:"
          :value="enrolledCount"
          v-if="!hideNonEditable"
        />
        <labeled-value
          class="mb-3"
          label="Avg. rating:"
          v-if="!hideNonEditable"
        >
          <div class="d-flex align-center">
            <rating-stars
              color="#945EFF"
              :rating="rating"
              :size="12"
            />&nbsp;<strong>{{ rating }}</strong>
          </div>
        </labeled-value>
        <labeled-value class="mb-3" label="Visibility:">
          <chip-select
            color="var(--bh-white)"
            :items="COURSES_VISIBILITY_STATUS_OPTIONS"
            :value="visibilityStatus"
            :readonly="readonly || (isCompanyUser && !isAdvisor)"
            @change="updateVisibilityStatus($event)"
          />
        </labeled-value>
        <p v-if="!hideNonEditable && visibilityStatus === 'DRAFT'">
          Assigned participants can be managed only when the course is no longer
          in Draft status.
        </p>
      </div>
    </div>
    <div class="row" v-if="!readonly">
      <div class="col-8">
        <v-btn
          color="accent"
          class="rounded-pill"
          type="submit"
          :disabled="isUpdateButtonDisabled"
          >{{ course && course.id ? 'Update' : 'Create' }}</v-btn
        >
      </div>
      <div class="col-4">
        <v-btn
          color="bhRed"
          class="rounded-pill"
          outlined
          @click="deleteCourse"
          v-if="course && course.id && !readonly"
          >Delete</v-btn
        >
      </div>
    </div>
  </v-form>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { COURSES_VISIBILITY_STATUS_OPTIONS } from '@/helpers/constants.js'
import ThumbnailPicker from '@/components/inputs/ThumbnailPicker.vue'
import BhTextInput from '@/components/inputs/BhTextInput.vue'
import BhTextArea from '@/components/inputs/BhTextArea.vue'
import LabeledValue from '@/components/labeledValue/LabeledValue.vue'
import RatingStars from '@/components/exercises/RatingStars.vue'
import AvatarsGroup from '@/components/avatars/AvatarsGroup.vue'
import ChipSelect from '@/components/inputs/ChipSelect.vue'

const emptyForm = {
  title: '',
  description: '',
  thumbnailValue: null,
}

export default {
  name: 'CourseForm',
  components: {
    ThumbnailPicker,
    BhTextInput,
    BhTextArea,
    LabeledValue,
    RatingStars,
    AvatarsGroup,
    ChipSelect,
  },
  mixins: [],
  props: {
    readonly: {
      type: Boolean,
      default: false,
    },
    hideNonEditable: {
      type: Boolean,
      default: false,
    },
    course: {
      type: Object,
    },
  },
  data() {
    return {
      COURSES_VISIBILITY_STATUS_OPTIONS,
      title: '',
      description: '',
      creatorName: '',
      createdDate: this.$moment().toISOString(),
      rating: 0,
      visibilityStatus: 'DRAFT',
      assignmentStatus: 'SELECTED',
      enrolledCount: 0,
      participantsCount: 0,
      participants: [],
      id: null,
      thumbnailUrl: null,
      mediaUrl: null,
      thumbnailValue: null,
      forceRules: false,
      staticMediaThumbnailUrl: null,
      form: emptyForm,
    }
  },
  computed: {
    ...mapGetters('dictionariesStates', ['getMimeTypes']),
    ...mapState('dictionariesStates', [
      'dictionariesLoaded',
      'dictionaryPracticeUserRole',
      'dictionaryBhUsersRoleIds',
      'dictionaryFeatureFlagState',
    ]),
    ...mapState('courseStates', ['loadingCourseDetails']),
    ...mapState('userStates', ['user']),
    ...mapGetters('userStates', ['getRolename', 'getToken']),
    createdDateFormatted() {
      return this.$moment(this.createdDate).format('MMMM, Do YYYY')
    },
    currentFile() {
      // this if is just to force refresh this variable on every thumbnailValue change
      if (this.thumbnailValue || !this.thumbnailValue) {
        if (this.staticMediaThumbnailUrl) {
          return null
        }
        if (!this.thumbnailUrl) {
          return null
        }
      }
      return {
        thumbnailUrl: this.thumbnailUrl,
        mediaUrl: this.mediaUrl || null,
      }
    },
    currentMobileStaticMedia() {
      // this if is just to force refresh this variable on every thumbnailValue change
      if (this.thumbnailValue || !this.thumbnailValue) {
        if (!this.staticMediaThumbnailUrl) {
          return null
        }
      }
      return {
        mediaUrl: this.staticMediaThumbnailUrl,
      }
    },
    authorName() {
      const creator = this.course ? this.creatorName : this.user

      if (!creator) return 'Unknown'

      let fullName = ''
      fullName += creator?.firstname || creator?.firstName || ''

      if (fullName !== 0) fullName += ' '
      fullName += creator?.lastname || creator?.lastName || ''

      return fullName
    },
    assignmentStatusLabel() {
      if (this.assignmentStatus === 'ALL') {
        return 'All'
      }
      return 'Selected'
    },
    participantsAvatars() {
      return this.participants?.map(el => el.thumbnailUrl)
    },
    isCompanyUser() {
      // is Company Admin, Company Advisor or Company Team
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ===
          this.dictionaryPracticeUserRole.Advisor ||
        this.dictionaryPracticeUserRole[this.getRolename] ===
          this.dictionaryPracticeUserRole.Administrator ||
        this.dictionaryPracticeUserRole[this.getRolename] ===
          this.dictionaryPracticeUserRole.Team
      )
    },
    isAdvisor() {
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ===
        this.dictionaryPracticeUserRole.Advisor
      )
    },
    formIsValid() {
      return (
        this.form.title &&
        this.form.title.length > 0 &&
        this.form.title.length <= 255 &&
        this.form.description &&
        this.form.description.length > 0 &&
        this.form.description.length <= 30000
      )
    },
    formIsDirty() {
      // Check if any of the editable fields have changed
      const titleChanged = this.title !== this.form.title
      const descriptionChanged = this.description !== this.form.description
      const thumbnailChanged = this.thumbnailValue !== this.form.thumbnailValue

      return titleChanged || descriptionChanged || thumbnailChanged
    },
    isUpdateButtonDisabled() {
      // For existing courses, disable if no changes or form is invalid
      if (this.id) {
        return !this.formIsDirty || !this.formIsValid
      }
      // For new courses, only check form validity
      return !this.formIsValid
    },
  },
  mounted() {
    this.coursePropUpdated(this.course)
    if (this.isCompanyUser && !this.isAdvisor) {
      this.updateField('visibilityStatus', 'ALL')
    }
  },
  methods: {
    isFormEmpty() {
      return (
        this.form.title === emptyForm.title &&
        this.form.description === emptyForm.description &&
        this.form.thumbnailValue === emptyForm.thumbnailValue
      )
    },
    checkIfUrlChanged(oldUrl, newUrl) {
      const oldUrlWithoutQuery = `${oldUrl}`.split('?')[0]
      const newUrlWithourQuery = `${newUrl}`.split('?')[0]
      return oldUrlWithoutQuery !== newUrlWithourQuery
    },
    isThumbnailValueTheSameAfterSave(oldValue, newValue) {
      return (
        (oldValue?.remove && newValue?.remove) ||
        (newValue?.mlSourceResourceId &&
          oldValue?.mlSourceResourceId === newValue?.mlSourceResourceId) ||
        (newValue?.id && oldValue?.id === newValue?.id) ||
        (newValue?.staticMediaId &&
          oldValue?.staticMediaId === newValue?.staticMediaId)
      )
    },
    coursePropUpdated(course = null) {
      if (course) {
        const {
          title,
          description,
          rating,
          createdBy,
          createdDate,
          visibilityStatus,
          assignmentStatus,
          enrolledCount,
          participantsCount,
          participants,
          thumbnailUrl,
          staticMediaThumbnailUrl,
          mediaUrl,
          id,
        } = course
        this.title = title || ''
        this.description = description || ''
        this.rating = rating || 0
        this.creatorName = createdBy
        this.createdDate = createdDate
        this.visibilityStatus = visibilityStatus
        this.assignmentStatus = assignmentStatus
        this.enrolledCount = enrolledCount || 0
        this.participantsCount = participantsCount || 0
        this.participants = participants || []
        this.id = id

        const isNewThumbnail = this.isThumbnailValueTheSameAfterSave()

        this.thumbnailUrl = thumbnailUrl
        this.mediaUrl = mediaUrl
        this.staticMediaThumbnailUrl = staticMediaThumbnailUrl

        if (isNewThumbnail) {
          this.thumbnailValue = null
          this.form.thumbnailValue = null
        }

        if (this.isFormEmpty()) {
          this.form = {
            title: title || '',
            description: description || '',
            thumbnailValue: null,
          }
        }
      } else {
        this.form = { ...emptyForm }
      }
    },
    setThumbnail(thumbnail = null) {
      if (thumbnail === null) {
        // remove
        this.form.thumbnailValue = { remove: true }
      } else {
        const { type, file } = thumbnail

        if (type === 'media-library') {
          this.form.thumbnailValue = { mlSourceResourceId: file.id }
        } else if (type === 'file-picker') {
          this.thumbnailUrl = null
          this.mediaUrl = null
          this.form.thumbnailValue = file
        } else if (type === 'mobile-static-media') {
          this.form.thumbnailValue = { staticMediaId: file.id }
        }
      }
    },
    updateField(fieldName, value) {
      const availableUpdateFields = ['title', 'description', 'thumbnailValue']
      if (availableUpdateFields.includes(fieldName)) {
        this.form[fieldName] = value
      }
    },
    submitForm() {
      if (!this.formIsValid) {
        this.forceRules = true
        return
      }

      this.thumbnailValue = this.form.thumbnailValue

      this.forceRules = false
      this.$emit('submit', {
        id: this.id,
        file: this.form.thumbnailValue,
        title: this.form.title,
        description: this.form.description,
        visibilityStatus: this.visibilityStatus,
        successMessage: 'Course updated!',
      })
    },
    deleteCourse() {
      if (this.course) {
        this.$emit('open-delete-course', this.course)
      }
    },
    openAssign() {
      this.$emit('open-assign', this.course)
    },
    refreshCurrentFile() {
      this.$emit('refresh-course-media')
    },
    updateVisibilityStatus(newStatus) {
      if (!this.id) {
        this.visibilityStatus = newStatus
        return
      }

      this.$emit('submit', {
        id: this.id,
        file: null,
        title: this.title,
        description: this.description,
        visibilityStatus: newStatus,
        successMessage: 'Course visibility updated!',
      })
    },
  },
  watch: {
    course(val) {
      this.coursePropUpdated(val)
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.course-form {
  max-width: 820px;
  min-width: 600px;

  &__description {
    .bh-text-area__input {
      flex: initial;
      background: $bh-white;
      min-height: 128px;
    }
    label.bh-text-area__label {
      color: $bh-gray--2;
    }
  }
}
</style>
